import React, {
  type FC,
  forwardRef,
  type ForwardRefRenderFunction,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";

import { useZenMode } from "../common/context/ZenModeContext";
import { IoChevronUpOutline } from "react-icons/io5";
import { AppRoute } from "../Routes";
import { useHelp } from "../common/context/HelpContext";
import { useWallet } from "@solana/wallet-adapter-react";
import { LinkWithQuery } from "../common/components/LinkWithQuery";
import { ReferTweetButton } from "./ReferTweetButton";
import { CopyButton } from "./CopyButton";
import { TopicContainer } from "../common/container/TopicContainer";
import { Card } from "../common/container/Card";
import { QRCodeCard } from "./QRCodeCard";
import { FaShareSquare } from "react-icons/fa";
import { Tweet } from "../common/components/Tweet";
import { ShareButton } from "./ShareButton";

const Title: FC = () => (
  <div className="topic-title w-full mt-8">
    <LinkWithQuery
      to="/"
      className="flex items-center text-green justify-center"
    >
      <div className="flex items-center nowrap">
        <IoChevronUpOutline className="inline" size={48} />
      </div>
    </LinkWithQuery>
    <h1 className="font-bold text-green-light text-4xl text-center mb-4">
      Share Your Referral Link
    </h1>
  </div>
);

const _ReferralApp: ForwardRefRenderFunction<
  HTMLDivElement,
  { className?: string; active?: boolean } & React.HTMLAttributes<HTMLElement>
> = ({ className, active = false, ...rest }, ref) => {
  const { currentHelpRoute } = useHelp();
  const [, updateZenMode] = useZenMode();

  const navigate = useNavigate();
  const wallet = useWallet();
  useEffect(() => {
    if (!wallet.connected && active) navigate("/");
  }, [active, wallet.connected]);

  const link =
    wallet.publicKey === null
      ? null
      : `https://app.sunrisestake.com?referrer=${wallet.publicKey.toBase58()}`;

  useEffect(() => {
    if (currentHelpRoute !== AppRoute.Lock) return; // we are not on the lock page, so don't update zen mode
    updateZenMode({
      showBGImage: false,
      showHelpButton: true,
      showExternalLinks: false,
      showWallet: active,
    });
  }, [active, currentHelpRoute]);

  const isMobile = window.navigator?.canShare?.();

  return (
    <TopicContainer
      className={className}
      ref={ref}
      {...rest}
      titleContents={<Title />}
    >
      {link === null && (
        <div className="flex flex-col items-center m-4">
          <h1 className="text-3xl text-center">Loading...</h1>
          <div
            className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full mt-4"
            role="status"
          ></div>
        </div>
      )}
      {link !== null && (
        <div className="mx-4 mb-3 flex flex-col items-center">
          <Card size="large" orientation="horizontal">
            <div className="w-full h-full md:space-x-8 space-y-4 md:space-y-0 justify-around items-center flex flex-col-reverse md:flex-row">
              {isMobile && (
                <div className="flex flex-row justify-around w-full mt-5 items-center">
                  <ShareButton link={link}>
                    <FaShareSquare className="text-white w-8 h-8" />
                  </ShareButton>
                  <Tweet
                    url={link}
                    tweet="Join my forest on Sunrise Stake! Join me and the Solana community in making a collective positive environmental impact."
                  />
                </div>
              )}
              {!isMobile && (
                <>
                  <CopyButton link={link}>
                    <Card
                      title={"Copy to Clipboard"}
                      size="medium"
                      image={
                        <img
                          src="/clip.png"
                          className="w-full h-full object-contain"
                          alt="Copy"
                        />
                      }
                    ></Card>
                  </CopyButton>
                  <Card
                    size="medium"
                    title={"Share"}
                    image={
                      <ReferTweetButton link={link}>
                        <img src="/sowing.png" alt="Share link" />
                      </ReferTweetButton>
                    }
                  ></Card>
                </>
              )}
              <QRCodeCard link={link} type="sunrise" />
              <QRCodeCard link={link} type="solanapay" />
            </div>
            <div className="hidden md:flex flex-row text-lg w-4/5 ">
              <div className="mr-2 font-bold basis-1/3">
                Your Referral Link:
              </div>
              <a className="text-sm text-ellipsis overflow-hidden" href={link}>
                {link}
              </a>
            </div>
          </Card>
        </div>
      )}
    </TopicContainer>
  );
};

const ReferralApp = forwardRef(_ReferralApp);

export { ReferralApp };
